/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-shadow */
/* eslint-disable init-declarations */
import React, { useState, useContext, useEffect, useRef } from 'react';
import { GlobalContext } from 'providers/GlobalProvider';
import { LoadingFeedback } from 'components/Common/LoadingModal';
import format from 'date-fns/format';
import { Modal } from '@mui/material';
import CreateTicketWizard from 'components/Wizard/CreateTicketWizard';
import { handleShareTicketNotification } from 'utils/NotificationsFuncs';
import { useSnackbar } from 'notistack';
import CasesAPI from 'api/CasesAPI';
import DatahubAPI from 'api/DatahubAPI';
import ftaMappingFile from 'components/TariffChecker/FTAMappings.json';
import { runAIExplanationOnly } from 'api/PyAPIConnect';
import {
  COLUMN2_ISO2,
  digitsOnly,
  formatHtsPretty,
  isFreeBase,
  normalizeCountry,
} from 'utils/tariffCodes';
import {
  buildEngineInputs,
  engineFigures,
  runEngine,
  SUPERSEDED,
} from 'utils/tariffEngine';
import CostSummaryComp from 'components/TariffChecker/CostSummaryComp';
import ProductDescComp from 'components/TariffChecker/ProductDescComp';
import DutyComp from 'components/TariffChecker/DutyComp';
import ExpComp from 'components/TariffChecker/ExpComp';
import { countries } from 'utils/countriesWithCodes';
import TariffToolInsightsPanel from 'components/TariffChecker/TariffToolInsightsPanel';
import { useSaveTariffResult } from 'api/bitedb/tariffs/tariffs.hooks';
import {
  BanGateAnswer,
  ImportBan,
  findActiveImportBan,
  importBanGateAnswer,
} from 'utils/importBans';

const FTA: any = ftaMappingFile.fta_mapping;

/* -------------------- Types -------------------- */
type ResultRow = {
  type: string;
  code: string;
  rate: string;
  _codeDigits?: string;
  _requiresLineSplit?: boolean;
  _lineSplitLabel?: string;
  _excludedByDigits?: string[];
  _priority?: number;
  _variantSingleLine?: boolean;
  _relatesDigits?: string[];
};

type HtsHistoryItem = {
  id: string;
  date: string;
  summary: string;
};

type RiskFlag = {
  id: string;
  title: string;
  description: string;
  severity: 'high' | 'medium' | 'info';
};

type Candidate = {
  label: string;
  codeDigits: string;
  codePretty: string;
  ratePct: number;
  rateDisplay: string;
  excludedByDigits: string[];
  requiresLineSplit: boolean;
  lineSplitKey?: string;
  lineSplitLabel?: string;
  bd_offer_choice: boolean;
  relatesDigits: string[];
  variantSingleLine: boolean;
  applicability?: any[];
  hasCountryCond?: boolean;
  _needsLoadingCountryForThis?: boolean;
  countryKeys?: string[];
  _failedSplitGate?: boolean;
  _failedCountryCheck?: boolean;
  _keepForDiscovery: boolean;
  applicabilityOk?: boolean;
  hasValidityCriteria?: boolean;
  bd_232_question?: string;
  bd_232_answer?: string;
};

type Line = {
  id: string;
  label: string;
  value: number;
  rows: ResultRow[];
  order?: number;
};

type FtaOption = { code: string; name: string };
type TariffStatus = { color: 'Green' | 'Yellow' | 'Red'; message: string };

interface AuditType {
  isExpands?: boolean;
  isSelected?: boolean;
  primary?: string;
  country?: string;
  handleOpenModal?: any;
  onCalculationDone?: (results: any) => void;
  productVal: number;
  materialPct?: number;
  effectDates: Date | null;
  loadDates: Date | null;
  inputs?: any;
  loadingCountryValue?: string;
  amountInputsValue?: Record<string, number>;
  materialPctByKeyValue?: Record<string, number>;
  enabledExclusionsValue?: Record<string, boolean>;
  setEnabledExclusionsValue?: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  enabledFtasValue?: Record<string, boolean>;
  setEnabledFtasValue?: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  setLineSplitShow?: React.Dispatch<React.SetStateAction<boolean>>;
  setLoadingCountryShow?: React.Dispatch<React.SetStateAction<boolean>>;
  setAmmountShow?: React.Dispatch<React.SetStateAction<boolean>>;
  setCountryFieldKey?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setCountryFieldCodes?: React.Dispatch<React.SetStateAction<string[]>>;
}

const TariffResultsComp: React.FC<AuditType> = ({
  isExpands,
  isSelected,
  primary,
  country,
  onCalculationDone,
  productVal,
  loadDates,
  effectDates,
  inputs,
  loadingCountryValue,
  amountInputsValue,
  materialPctByKeyValue,
  enabledExclusionsValue,
  setEnabledExclusionsValue,
  enabledFtasValue,
  setEnabledFtasValue,
  setLineSplitShow: setParentLineSplitShow,
  setLoadingCountryShow: setParentLoadingCountryShow,
  setAmmountShow: setParentAmmountShow,
  setCountryFieldKey: setParentCountryFieldKey,
  setCountryFieldCodes: setParentCountryFieldCodes,
}) => {
  const [isExpand, setIsExpand] = useState(isExpands || false);
  const [isExpandProd, setIsExpandProd] = useState(true);
  const [isExpandSecNotes, setIsExpandSecNotes] = useState(true);
  const [isExpandCross, setIsExpandCross] = useState(true);
  const [isExpandFlags, setIsExpandFlags] = useState(true);
  const { cur_user, ticketDraft, setTicketDraft } = useContext(GlobalContext);

  const [loading, setLoading] = useState(false);
  const [aiLoading, setAILoading] = useState(false);
  const [tariffResults, setTariffResults] = useState<any>({
    isLoading: false,
    data: {},
  });

  const [effectDate, setEffectDate] = useState(
    effectDates ? effectDates : new Date(),
  );
  const [loadDate, setLoadDate] = useState(loadDates ? loadDates : new Date());
  const [productValue, setProductValue] = useState(
    productVal ? productVal : 1000,
  );
  const [frequency, setFrequency] = useState<string>('Once');
  const [dateStrategy, setDateStrategy] = useState<'current' | 'original'>(
    'original',
  );

  const [tariffCandidates, setTariffCandidates] = useState<Candidate[]>([]);
  const [importBans, setImportBans] = useState<ImportBan[]>([]);
  const [banGateAnswers, setBanGateAnswers] = useState<
    Record<string, BanGateAnswer>
  >({});
  const activeImportBan = findActiveImportBan(importBans, banGateAnswers);
  const [enabledExclusions, setEnabledExclusions] = useState<
    Record<string, boolean>
  >({});

  const [loadingCountryShow, setLoadingCountryShow] = useState(false);

  const [enabledFtas, setEnabledFtas] = useState<Record<string, boolean>>({});
  const [loadingCountry, setLoadingCountry] = useState<string>(
    country ? country : '',
  );
  const [countryFieldKey, setCountryFieldKey] = useState<string | undefined>(
    undefined,
  );
  const [explanationText, setExplanationText] = useState('');
  const [countryFieldCodes, setCountryFieldCodes] = useState<string[]>([]);
  const [amountInputs, setAmountInputs] = useState<Record<string, number>>({
    kg: 10,
  });
  const [materialPctByKey, setMaterialPctByKey] = useState<
    Record<string, number>
  >({});
  const [adcvdResults, setAdcvdResults] = useState<any>('');

  const { enqueueSnackbar } = useSnackbar();
  const dt = new Date();
  const dtWithoutTz = new Date(
    dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000,
  );
  const formatted_dt = format(dtWithoutTz, 'yyyy-MM-dd');

  const [saving, setSaving] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [newTicketName, setNewTicketName] = useState<string>('');
  const [shareEmail, setShareEmail] = useState<string[]>([]);
  const [htsDocument, setHtsDocument] = useState<any>({});

  const [userhasTariffs, setUserHasTariffs] = useState(false);
  const [htsHistory, setHtsHistory] = useState<HtsHistoryItem[]>([]);
  const [riskFlags, setRiskFlags] = useState<RiskFlag[]>([]);
  const [ammountShow, setAmmountShow] = useState(false);
  const forceStatic = false;
  // If we have an ID, start in view-only mode. Otherwise (standalone), edit mode is always true.
  const [isEditMode, setIsEditMode] = useState(false);
  const isStaticView = false;

  const handleCloseModal = () => setModalOpen(false);
  // Track whether we previously had a split, so we can clean up when it turns off
  const hydratingRef = React.useRef(false);
  const [lineSplitShow, setLineSplitShow] = useState(false);
  const parentUiSyncRef = React.useRef({
    lineSplitShow: false,
    loadingCountryShow: false,
    ammountShow: false,
    countryFieldKey: undefined as string | undefined,
    countryFieldCodes: [] as string[],
  });

  const sameStringArray = (a: string[], b: string[]) =>
    a.length === b.length && a.every((value, index) => value === b[index]);
  const sameBoolMap = (
    a: Record<string, boolean>,
    b: Record<string, boolean>,
  ) => {
    const aKeys = Object.keys(a || {});
    const bKeys = Object.keys(b || {});
    return (
      aKeys.length === bKeys.length &&
      aKeys.every((key) => Boolean(a[key]) === Boolean(b[key]))
    );
  };

  const syncEnabledFtas = (value: Record<string, boolean>) => {
    setEnabledFtas((prev) => (sameBoolMap(prev, value) ? prev : value));
    setEnabledFtasValue?.((prev) =>
      sameBoolMap(prev || {}, value) ? prev : value,
    );
  };

  const syncEnabledExclusions = (value: Record<string, boolean>) => {
    setEnabledExclusions((prev) => (sameBoolMap(prev, value) ? prev : value));
    setEnabledExclusionsValue?.((prev) =>
      sameBoolMap(prev || {}, value) ? prev : value,
    );
  };

  const syncLineSplitShow = (value: boolean) => {
    setLineSplitShow((prev) => (prev === value ? prev : value));
    if (parentUiSyncRef.current.lineSplitShow !== value) {
      parentUiSyncRef.current.lineSplitShow = value;
      setParentLineSplitShow?.(value);
    }
  };

  const syncLoadingCountryShow = (value: boolean) => {
    setLoadingCountryShow((prev) => (prev === value ? prev : value));
    if (parentUiSyncRef.current.loadingCountryShow !== value) {
      parentUiSyncRef.current.loadingCountryShow = value;
      setParentLoadingCountryShow?.(value);
    }
  };

  const syncAmmountShow = (value: boolean) => {
    setAmmountShow((prev) => (prev === value ? prev : value));
    if (parentUiSyncRef.current.ammountShow !== value) {
      parentUiSyncRef.current.ammountShow = value;
      setParentAmmountShow?.(value);
    }
  };

  const syncCountryFieldKey = (value: string | undefined) => {
    setCountryFieldKey((prev) => (prev === value ? prev : value));
    if (parentUiSyncRef.current.countryFieldKey !== value) {
      parentUiSyncRef.current.countryFieldKey = value;
      setParentCountryFieldKey?.(value);
    }
  };

  const syncCountryFieldCodes = (value: string[]) => {
    setCountryFieldCodes((prev) =>
      sameStringArray(prev, value) ? prev : value,
    );
    if (!sameStringArray(parentUiSyncRef.current.countryFieldCodes, value)) {
      parentUiSyncRef.current.countryFieldCodes = value;
      setParentCountryFieldCodes?.(value);
    }
  };

  const getIso2ByCountryName = (name?: string): string | undefined => {
    if (!name) {
      return '';
    }

    const country = countries.find(
      (c) => c.name.toLowerCase() === name.toLowerCase(),
    );

    return country?.iso2 || '';
  };

  const [tariffInputs, setTariffInputs] = useState<any>({
    htsCode: primary
      ? primary
      : ticketDraft.commodity?.harmonizedScheduleCode
      ? ticketDraft.commodity?.harmonizedScheduleCode
      : '',
    originCountry: country ? country : '',
    originIso2: getIso2ByCountryName(country ? country : ''),
    materialPct: 40,
    amountValue: productVal ? productVal : 100,
    amountUnit: 'kg',
    loadingCountry: country ? country : '',
    transportMethod: 'Ocean',
  });

  useEffect(() => {
    setProductValue((prev) =>
      prev === productVal ? prev : productVal || 1000,
    );
  }, [productVal]);

  useEffect(() => {
    if (!effectDates) {
      return;
    }
    const nextTime = effectDates.getTime();
    setEffectDate((prev) =>
      prev?.getTime?.() === nextTime ? prev : effectDates,
    );
  }, [effectDates]);

  useEffect(() => {
    if (!loadDates) {
      return;
    }
    const nextTime = loadDates.getTime();
    setLoadDate((prev) => (prev?.getTime?.() === nextTime ? prev : loadDates));
  }, [loadDates]);

  useEffect(() => {
    if (!inputs) {
      return;
    }

    setTariffInputs((prev: any) => {
      const next = {
        ...prev,
        htsCode: inputs.htsCode ?? prev.htsCode ?? '',
        originCountry: inputs.originCountry ?? prev.originCountry ?? '',
        originIso2:
          inputs.originIso2 ??
          getIso2ByCountryName(
            inputs.originCountry ?? prev.originCountry ?? '',
          ),
        materialPct: inputs.materialPct ?? prev.materialPct ?? 40,
        amountValue: inputs.amountValue ?? prev.amountValue ?? 10,
        amountUnit: inputs.amountUnit ?? prev.amountUnit ?? 'kg',
        loadingCountry: inputs.loadingCountry ?? prev.loadingCountry ?? '',
        transportMethod:
          inputs.transportMethod ?? prev.transportMethod ?? 'Ocean',
      };

      const same =
        prev.htsCode === next.htsCode &&
        prev.originCountry === next.originCountry &&
        prev.originIso2 === next.originIso2 &&
        prev.materialPct === next.materialPct &&
        prev.amountValue === next.amountValue &&
        prev.amountUnit === next.amountUnit &&
        prev.loadingCountry === next.loadingCountry &&
        prev.transportMethod === next.transportMethod;

      return same ? prev : next;
    });
  }, [inputs]);

  useEffect(() => {
    if (loadingCountryValue === null || loadingCountryValue === undefined) {
      return;
    }
    setLoadingCountry((prev) =>
      prev === loadingCountryValue ? prev : loadingCountryValue,
    );
  }, [loadingCountryValue]);

  useEffect(() => {
    if (!amountInputsValue) {
      return;
    }
    setAmountInputs((prev) => {
      const prevKeys = Object.keys(prev || {});
      const nextKeys = Object.keys(amountInputsValue || {});
      const same =
        prevKeys.length === nextKeys.length &&
        nextKeys.every((key) => prev[key] === amountInputsValue[key]);
      return same ? prev : amountInputsValue;
    });
  }, [amountInputsValue]);

  useEffect(() => {
    if (!materialPctByKeyValue) {
      return;
    }
    setMaterialPctByKey((prev) => {
      const prevKeys = Object.keys(prev || {});
      const nextKeys = Object.keys(materialPctByKeyValue || {});
      const same =
        prevKeys.length === nextKeys.length &&
        nextKeys.every((key) => prev[key] === materialPctByKeyValue[key]);
      return same ? prev : materialPctByKeyValue;
    });
  }, [materialPctByKeyValue]);

  useEffect(() => {
    if (!enabledFtasValue) {
      return;
    }
    if (sameBoolMap(enabledFtas, enabledFtasValue)) {
      return;
    }

    setEnabledFtas(enabledFtasValue);
    if (tariffResults?.data?.baseDutyRateRaw) {
      cascadeRebuild(enabledFtasValue, undefined);
    }
  }, [enabledFtasValue]);

  useEffect(() => {
    if (!enabledExclusionsValue) {
      return;
    }
    if (sameBoolMap(enabledExclusions, enabledExclusionsValue)) {
      return;
    }

    setEnabledExclusions(enabledExclusionsValue);
    if (tariffResults?.data?.baseDutyRateRaw) {
      cascadeRebuild(undefined, enabledExclusionsValue);
    }
  }, [enabledExclusionsValue]);

  const TCDBG = {
    // enable via localStorage or window flags
    get enabled() {
      return (
        localStorage.getItem('tc.debug') === '1' ||
        (globalThis as any).__TC_DEBUG__ === true
      );
    },
    get code() {
      const ls = localStorage.getItem('tc.debug.code') || '';
      const win = (globalThis as any).__TC_DEBUG_CODE__ || '';
      return digitsOnly(ls || win);
    },
    log: function (codeDigits: string | undefined, msg: string, extra?: any) {
      if (!this.enabled) {
        return;
      }
      const want = this.code;
      if (want && digitsOnly(codeDigits || '') !== want) {
        return;
      }
      try {
        console.error(`[TC DEBUG ${codeDigits || ''}] ${msg}`, extra ?? '');
      } catch {}
    },
  };

  // handy console helpers
  (globalThis as any).tcDebugOn = (code = '') => {
    localStorage.setItem('tc.debug', '1');
    localStorage.setItem('tc.debug.code', digitsOnly(code));
  };
  (globalThis as any).tcDebugOff = () => {
    localStorage.removeItem('tc.debug');
    localStorage.removeItem('tc.debug.code');
  };

  /* -------------------- License -------------------- */
  const requiredTiers = ['tariffs', 'starter', 'pro', 'business', 'enterprise'];

  /* -------------------- License -------------------- */
  const checkLicense = () => {
    if (
      requiredTiers.some((role) =>
        cur_user.selected_team?.attributes?.tier.includes(role),
      )
    ) {
      setUserHasTariffs(true);
    }
  };

  useEffect(() => {
    checkLicense();
  }, []);
  const saveTariffResultMutation = useSaveTariffResult();

  const getSavedSnapshot = React.useCallback(() => {
    if (forceStatic) {
      return {};
    }
  }, [forceStatic]);

  const savedBulkStatus = React.useMemo(() => {
    const status = (getSavedSnapshot() as any)?.status;
    if (!status) {
      return null;
    }

    const color =
      status?.color === 'Green' ||
      status?.color === 'Yellow' ||
      status?.color === 'Red'
        ? status.color
        : null;

    if (!color) {
      return null;
    }

    return {
      color: color,
      message: String(status?.message || ''),
    } as TariffStatus;
  }, [getSavedSnapshot]);

  const effectiveBulkStatus = savedBulkStatus;
  const showBulkStatusBanner =
    isStaticView && Boolean(forceStatic) && Boolean(effectiveBulkStatus);

  // --- INJECTED: Hydrate State from DB ---
  useEffect(() => {
    const normalizeBoolMap = (val: any): Record<string, boolean> => {
      if (!val) {
        return {};
      }
      if (
        typeof val?.get === 'function' &&
        typeof val?.entries === 'function'
      ) {
        const out: Record<string, boolean> = {};
        for (const [k, v] of val.entries()) {
          out[k] = Boolean(v);
        }
        return out;
      }
      if (Array.isArray(val)) {
        try {
          return Object.fromEntries(val);
        } catch {
          return {};
        }
      }
      if (typeof val === 'object') {
        return val as Record<string, boolean>;
      }
      return {};
    };

    const savedTR = getSavedSnapshot() as any;

    if (!savedTR || !isStaticView) {
      return;
    }

    // 3. Extract sections
    const inputs = savedTR.inputs || {};
    const results = savedTR.results || {};
    const base = savedTR.base || {};
    const math = savedTR.math || {};

    setProductValue(inputs.productValue ?? 1000);

    // Saved Work uses effectDate/loadDate, while some bulk shapes may have entryDate/loadingDate
    const savedEntryDate = inputs.effectDate || inputs.entryDate;
    if (savedEntryDate) {
      setEffectDate(new Date(savedEntryDate));
    }

    const savedLoadDate = inputs.loadDate || inputs.loadingDate;
    if (savedLoadDate) {
      setLoadDate(new Date(savedLoadDate));
    }

    // 5. Hydrate Maps & Text
    const enabledMap = normalizeBoolMap(results.enabledExclusions);
    const enabledFtasMap = normalizeBoolMap(results.enabledFtas);
    syncEnabledExclusions(enabledMap);
    syncEnabledFtas(enabledFtasMap);
    setLoadingCountry(inputs.loadingCountry || '');
    setExplanationText(savedTR?.explanation?.text || '');
    setAdcvdResults(savedTR?.adcvdResults || results?.adcvdResults || '');
    setMaterialPctByKey(inputs.materialPctByKey || {});
    setAmountInputs(
      inputs.amountInputs || { kg: Number(inputs.amountValue) || 10 },
    );

    // --- NEW: Hydrate Insights ---
    if (savedTR.insights) {
      setHtsHistory(savedTR.insights.history || []);
      setRiskFlags(savedTR.insights.riskFlags || []);
    }

    // 6. SANITIZE LINES (The "Phantom" Fix)
    // We filter the lines *before* setting them into tariffResults.
    const rawLines = (results.lines || []) as Line[];
    const optionalPool = (results.optionalExclusions || []) as Candidate[];

    const sanitizedLines = rawLines.map((line) => ({
      ...line,
      rows: line.rows.filter((row) => {
        const digits = row._codeDigits || row.code.replace(/\D/g, '');

        // Is this row an optional exclusion?
        const isOptional = optionalPool.some(
          (opt) => opt.codeDigits === digits,
        );

        // If optional and NOT enabled, drop it.
        if (isOptional && !enabledMap[digits]) {
          return false;
        }
        return true;
      }),
    }));

    // 7. Set Tariff Results (Single Source of Truth)
    if (results.summaryRows || base.baseDutyRate) {
      // Set UI Flags
      syncLineSplitShow(Boolean(results.hasSplit));
      syncLoadingCountryShow(savedTR?.countryUi?.show || false);
      syncCountryFieldKey(savedTR?.countryUi?.key);
      syncCountryFieldCodes(savedTR?.countryUi?.codes || []);
      syncAmmountShow((results.requiredAmountUnits?.length || 0) > 0);
    }
  }, [getSavedSnapshot, isStaticView]);

  useEffect(() => {
    if (forceStatic) {
      setIsEditMode(false);
    }
  }, [forceStatic]);

  const lastCalculationSignatureRef = useRef('');
  const lastDraftSignatureRef = useRef('');

  useEffect(() => {
    const data = tariffResults?.data;
    const results = data?.results;

    if (Array.isArray(results) && results.length > 0 && onCalculationDone) {
      const signature = JSON.stringify({
        results: results,
        lines: data?.lines || [],
        baseDutyRate: data?.baseDutyRate || '',
        additionalDutyRate: data?.additionalDutyRate || '',
        requiredAmountUnits: data?.requiredAmountUnits || [],
        lineSplitFields: data?.lineSplitFields || [],
      });

      if (lastCalculationSignatureRef.current === signature) {
        return;
      }

      lastCalculationSignatureRef.current = signature;

      onCalculationDone(data);

      if (lastDraftSignatureRef.current !== signature) {
        lastDraftSignatureRef.current = signature;
        createDraftTicket('');
      }
    }
  }, [tariffResults.data, onCalculationDone]);

  // ---- helpers ----
  const normalizeLinesForSave = (
    uiLines: Line[] | undefined,
    fallbackRows: ResultRow[],
    value: number,
  ): Array<Line & { order: number }> => {
    if (!Array.isArray(uiLines) || uiLines.length === 0) {
      return [
        {
          id: 'base',
          label: 'Line 1',
          value: value,
          rows: JSON.parse(JSON.stringify(fallbackRows)),
          order: 1, // ✅ now allowed
        },
      ];
    }

    return uiLines.map((l, idx) => ({
      ...l,
      rows: JSON.parse(JSON.stringify(l.rows)),
      order: typeof l.order === 'number' ? l.order : idx + 1,
    }));
  };

  /* -------------------- Ticket creation -------------------- */
  const createTicket = async (name: string) => {
    setSaving(true);

    // ---- helpers ----
    const clone = <T,>(x: T): T => JSON.parse(JSON.stringify(x));

    const safeNumber = (n: any) => (Number.isFinite(Number(n)) ? Number(n) : 0);

    try {
      // What the user is currently seeing
      const trData = tariffResults?.data || {};
      const summaryRows: ResultRow[] = clone(
        (trData.results ?? []) as ResultRow[],
      );
      const uiLines: Line[] = Array.isArray(trData.lines)
        ? (trData.lines as Line[])
        : [];
      const baseRateDisplay = trData?.baseDutyRate ?? '';

      // Persist the lines exactly as shown (with stable order),
      // or synthesize a single base line when there are none.
      const linesForSave: Line[] = normalizeLinesForSave(
        uiLines,
        summaryRows,
        productValue,
      );

      // Build calc lines (same rows, current values).
      let calcLines: Line[] = linesForSave.map((l) => ({
        id: l.id,
        label: l.label,
        value: safeNumber(l.value),
        rows: clone(l.rows),
      }));

      // If base is "Free", zero Base Duty rows (both % and amount-based) before math.
      calcLines = applyFreeBaseToLines(calcLines, baseRateDisplay);

      // Duty math (uses your unified aggregator)
      // Duty figures come from the engine's own answer — the same numbers the
      // panel is showing. Recomputing them here is how a ticket could end up
      // carrying a different total than the screen it was created from, and
      // it needed a second copy of the duty math to do it.
      const engineMath = tariffResults?.data?.engineMath || {};
      const additional = Number(engineMath.additionalDutyDollars) || 0;
      const baseAmount = Number(engineMath.baseAmountDollars) || 0;
      const dutyCost = Number(engineMath.dutyCost) || 0;
      // The ad-valorem portion, back out of the total the engine reported.
      const basePctDollars = Math.max(0, dutyCost - additional - baseAmount);
      const transportMethodSnap = String(
        tariffInputs.transportMethod || 'Ocean',
      );

      const {
        mpf: mpfSnap,
        hmf: hmfSnap,
        totalCost: totalCost,
      } = engineFigures(engineMath);

      const additionalRateRaw = productValue
        ? (additional / productValue) * 100
        : 0;
      const additionalRateDisplay = `${additionalRateRaw
        .toFixed(2)
        .replace(/\.00$/, '')
        .replace(/(\.\d)0$/, '$1')}%`;

      const needsAmount = (trData?.requiredAmountUnits?.length ?? 0) > 0;
      const needsLoadingCountry = Boolean(loadingCountryShow);

      const inputs: Record<string, any> = {
        htsCode: tariffInputs.htsCode,
        originCountry: tariffInputs.originCountry,
        productValue: productValue,
        effectDate: new Date(effectDate).toISOString(),
        loadDate: new Date(loadDate).toISOString(),
        dateStrategy: dateStrategy,
        transportMethod: transportMethodSnap, // NEW
        ...(Array.isArray(trData?.lineSplitFields) &&
          trData.lineSplitFields.length > 0 && {
            materialPctByKey: { ...materialPctByKey },
          }),
        ...(needsAmount && {
          amountValue: tariffInputs.amountValue,
          amountUnit: tariffInputs.amountUnit,
          amountInputs: amountInputs,
        }),
        ...(needsLoadingCountry && { loadingCountry: loadingCountry }),
      };

      Object.keys(inputs).forEach((k) =>
        inputs[k] === undefined ? delete inputs[k] : null,
      );

      // Snapshot payload saved into the ticket
      const tariffSnapshot = {
        inputs: {
          ...inputs,
          // keep legacy single slider for back-compat (optional)
          materialPct: tariffInputs.materialPct,
        },
        base: {
          baseDutyRate: baseRateDisplay,
          productDesc: trData?.productDesc ?? '',
          lineSplitLabel:
            trData?.lineSplitLabel ??
            (Array.isArray(trData?.lineSplitFields) &&
            trData.lineSplitFields.length > 0
              ? 'Material Percentage'
              : ''),
        },
        results: {
          hasSplit: Array.isArray(uiLines) && uiLines.length > 1,
          summaryRows: summaryRows, // Duty Summary rows (deduped list)
          lines: linesForSave, // 👈 unlimited lines, IDs preserved, ordered
          optionalExclusions: clone(trData?.optionalExclusions ?? []),
          enabledExclusions: { ...enabledExclusions },
          ftaOptions: clone(trData?.ftaOptions ?? []),
          enabledFtas: { ...enabledFtas },
          lineSplitFields: clone(trData?.lineSplitFields ?? []),
          importBans: clone(importBans),
          banGateAnswers: { ...banGateAnswers },
          activeImportBan: activeImportBan ? clone(activeImportBan) : null,
        },
        countryUi: {
          show: Boolean(loadingCountryShow),
          key: countryFieldKey || '',
          codes: Array.isArray(countryFieldCodes) ? [...countryFieldCodes] : [],
        },
        math: {
          additionalDutyRate: additionalRateRaw,
          additionalDutyRateDisplay: additionalRateDisplay,
          additionalDutyDollars: additional,
          baseAmountDollars: baseAmount,
          dutyCost: dutyCost,
          mpf: mpfSnap,
          hmf: hmfSnap,
          transportMethod: transportMethodSnap,
          totalCost: totalCost,
        },

        explanation: {
          text: String(explanationText || ''),
          generatedAt: new Date().toISOString(),
        },
        savedAt: new Date().toISOString(),
        insights: {
          history: htsHistory,
          riskFlags: riskFlags,
        },
        adcvdResults: adcvdResults || '',
      };

      // ----- existing ticket payload scaffold (unchanged shape) -----
      const comFilterQuery = {
        lenses: [],
        countries: [],
        agencies: [],
        lists: [],
        hsCodeLists: [],
        sections: [],
        chapters: [],
        codes: [],
        entRes: false,
        filterDate: [],
      };
      const entFilterQuery = {
        lenses: [],
        countries: [],
        agencies: [],
        lists: [],
        entRes: false,
        filterDate: [],
      };
      const ownerQuery = {
        kc_id: cur_user.basics?.id,
        email: cur_user.basics?.email,
        firstName: cur_user.basics?.firstName,
        lastName: cur_user.basics?.lastName,
      };

      const query: any = {
        ticketName: name,
        lastUpdate: formatted_dt,
        commodity: {
          harmonizedScheduleCode: tariffInputs.htsCode,
          countryOfOrigin: tariffInputs.originCountry,
          destinationCountry: 'USA',
        },
        docType: 'Import Unified',
        commodityFilter: comFilterQuery,
        entityFilter: entFilterQuery,
        frequency: frequency || 'Once',
        sharedWith: shareEmail,
        activeFilter: '',
        selectedOpts: [],
        status: 'Complete',
        favorite: false,
        groupName: cur_user.selected_team.name,
        groupId: cur_user.selected_team.id,
        template: [],
        colMapping: [],
        activity: {
          user: {
            kc_id: cur_user.basics?.id,
            email: cur_user.basics?.email,
            firstName: cur_user.basics?.firstName,
            lastName: cur_user.basics?.lastName,
          },
          action: 'saved work',
          timeStamp: Date.now(),
        },
        owner: ownerQuery,

        // 👇 store the full, static snapshot here
        tariffResults: tariffSnapshot,
      };

      const resp = await CasesAPI.createCase({ newCase: query });
      if (resp?.success) {
        shareEmail.forEach((email) => {
          handleShareTicketNotification(name, cur_user, email, resp.result._id);
        });
        setSaving(false);
        handleCloseModal();
        enqueueSnackbar('Successfully created new case', {
          variant: 'success',
        });
      } else if (resp) {
        enqueueSnackbar(
          'Error saving new case. Uploaded file(s) may be too large.',
          { variant: 'error' },
        );
        setSaving(false);
      }
    } catch (err) {
      console.error('[createTicket] failed:', err);
      enqueueSnackbar('Error saving new case.', { variant: 'error' });
      setSaving(false);
    }
  };

  const createDraftTicket = async (name: string) => {
    setSaving(true);

    // ---- helpers ----
    const clone = <T,>(x: T): T => JSON.parse(JSON.stringify(x));

    const safeNumber = (n: any) => (Number.isFinite(Number(n)) ? Number(n) : 0);

    try {
      // What the user is currently seeing
      const trData = tariffResults?.data || {};
      const summaryRows: ResultRow[] = clone(
        (trData.results ?? []) as ResultRow[],
      );
      const uiLines: Line[] = Array.isArray(trData.lines)
        ? (trData.lines as Line[])
        : [];
      const baseRateDisplay = trData?.baseDutyRate ?? '';

      // Persist the lines exactly as shown (with stable order),
      // or synthesize a single base line when there are none.
      const linesForSave: Line[] = normalizeLinesForSave(
        uiLines,
        summaryRows,
        productValue,
      );

      // Build calc lines (same rows, current values).
      let calcLines: Line[] = linesForSave.map((l) => ({
        id: l.id,
        label: l.label,
        value: safeNumber(l.value),
        rows: clone(l.rows),
      }));

      // If base is 'Free', zero Base Duty rows (both % and amount-based) before math.
      calcLines = applyFreeBaseToLines(calcLines, baseRateDisplay);

      // Duty math (uses your unified aggregator)
      // Duty figures come from the engine's own answer — the same numbers the
      // panel is showing. Recomputing them here is how a ticket could end up
      // carrying a different total than the screen it was created from, and
      // it needed a second copy of the duty math to do it.
      const engineMath = tariffResults?.data?.engineMath || {};
      const additional = Number(engineMath.additionalDutyDollars) || 0;
      const baseAmount = Number(engineMath.baseAmountDollars) || 0;
      const dutyCost = Number(engineMath.dutyCost) || 0;
      // The ad-valorem portion, back out of the total the engine reported.
      const basePctDollars = Math.max(0, dutyCost - additional - baseAmount);
      const {
        mpf: mpfSnap,
        hmf: hmfSnap,
        totalCost: totalCost,
      } = engineFigures(engineMath);

      const additionalRateRaw = productValue
        ? (additional / productValue) * 100
        : 0;
      const additionalRateDisplay = `${additionalRateRaw
        .toFixed(2)
        .replace(/\.00$/, '')
        .replace(/(\.\d)0$/, '$1')}%`;

      const needsAmount = (trData?.requiredAmountUnits?.length ?? 0) > 0;
      const needsLoadingCountry = Boolean(loadingCountryShow);

      // Inputs stored on the ticket (include multi-split map if present)
      const inputs: Record<string, any> = {
        htsCode: tariffInputs.htsCode,
        originCountry: tariffInputs.originCountry,
        productValue: productValue,
        effectDate: new Date(effectDate).toISOString(),
        dateStrategy: dateStrategy,
        loadDate: new Date(loadDate).toISOString(),
        ...(Array.isArray(trData?.lineSplitFields) &&
          trData.lineSplitFields.length > 0 && {
            materialPctByKey: { ...materialPctByKey },
          }),
        ...(needsAmount && {
          amountValue: tariffInputs.amountValue,
          amountUnit: tariffInputs.amountUnit,
          amountInputs: amountInputs,
        }),
        ...(needsLoadingCountry && { loadingCountry: loadingCountry }),
      };
      Object.keys(inputs).forEach((k) =>
        inputs[k] === undefined ? delete inputs[k] : null,
      );

      // Snapshot payload saved into the ticket
      const tariffSnapshot = {
        inputs: {
          ...inputs,
          // keep legacy single slider for back-compat (optional)
          materialPct: tariffInputs.materialPct,
        },
        base: {
          baseDutyRate: baseRateDisplay,
          productDesc: trData?.productDesc ?? '',
          lineSplitLabel:
            trData?.lineSplitLabel ??
            (Array.isArray(trData?.lineSplitFields) &&
            trData.lineSplitFields.length > 0
              ? 'Material Percentage'
              : ''),
        },
        results: {
          hasSplit: Array.isArray(uiLines) && uiLines.length > 1,
          summaryRows: summaryRows, // Duty Summary rows (deduped list)
          lines: linesForSave, // 👈 unlimited lines, IDs preserved, ordered
          optionalExclusions: clone(trData?.optionalExclusions ?? []),
          enabledExclusions: { ...enabledExclusions },
          ftaOptions: clone(trData?.ftaOptions ?? []),
          enabledFtas: { ...enabledFtas },
          lineSplitFields: clone(trData?.lineSplitFields ?? []),
          importBans: clone(importBans),
          banGateAnswers: { ...banGateAnswers },
          activeImportBan: activeImportBan ? clone(activeImportBan) : null,
        },
        countryUi: {
          show: Boolean(loadingCountryShow),
          key: countryFieldKey || '',
          codes: Array.isArray(countryFieldCodes) ? [...countryFieldCodes] : [],
        },
        math: {
          additionalDutyRate: additionalRateRaw,
          additionalDutyRateDisplay: additionalRateDisplay,
          additionalDutyDollars: additional,
          baseAmountDollars: baseAmount,
          dutyCost: dutyCost,
          mpf: mpfSnap,
          hmf: hmfSnap,
          totalCost: totalCost,
        },
        explanation: {
          text: String(
            tariffResults.data?.explanation?.text || explanationText || '',
          ),
          generatedAt: new Date().toISOString(),
        },
        insights: {
          history: htsHistory,
          riskFlags: riskFlags,
        },
        adcvdResults: adcvdResults || '',
        savedAt: new Date().toISOString(),
      };

      // ----- existing ticket payload scaffold (unchanged shape) -----
      const comFilterQuery = {
        lenses: [],
        countries: [],
        agencies: [],
        lists: [],
        hsCodeLists: [],
        sections: [],
        chapters: [],
        codes: [],
        entRes: false,
        filterDate: [],
      };
      const entFilterQuery = {
        lenses: [],
        countries: [],
        agencies: [],
        lists: [],
        entRes: false,
        filterDate: [],
      };
      const ownerQuery = {
        kc_id: cur_user.basics?.id,
        email: cur_user.basics?.email,
        firstName: cur_user.basics?.firstName,
        lastName: cur_user.basics?.lastName,
      };

      const query: any = {
        ticketName: ticketDraft.ticketName ? ticketDraft.ticketName : name,
        lastUpdate: formatted_dt,
        docType: 'Import Unified',
        commodity: {
          harmonizedScheduleCode: tariffInputs.htsCode,
          commodityName: ticketDraft.commodity?.commodityName
            ? ticketDraft.commodity?.commodityName
            : '',
          countryOfOrigin: tariffInputs.originCountry,
          destinationCountry: '',
        },
        commodityFilter: comFilterQuery,
        entityFilter: entFilterQuery,
        frequency: 'Once',
        sharedWith: shareEmail,
        activeFilter: '',
        selectedOpts: [],
        status: 'Complete',
        favorite: false,
        groupName: cur_user.selected_team.name,
        template: [],
        colMapping: [],
        activity: {
          user: {
            kc_id: cur_user.basics?.id,
            email: cur_user.basics?.email,
            firstName: cur_user.basics?.firstName,
            lastName: cur_user.basics?.lastName,
          },
          action: 'created monitor ticket',
          timeStamp: Date.now(),
        },
        owner: ownerQuery,

        // 👇 store the full, static snapshot here
        tariffResults: tariffSnapshot,
      };

      const new_case = {
        groupId: cur_user.selected_team.id,
        ...query.basics,
        ...ticketDraft,
        ...query,
      };
      setTicketDraft({
        ...ticketDraft,
        ...new_case,
      });
    } catch (err) {
      console.error('[createTicket] failed:', err);
      enqueueSnackbar('Error saving new case.', { variant: 'error' });
      setSaving(false);
    }
  };

  /* -------------------- Helpers -------------------- */
  const resetTransactionState = () => {
    syncEnabledExclusions({});
    syncEnabledFtas({});
    setImportBans([]);
    setBanGateAnswers({});
    setMaterialPctByKey({});
    setTariffCandidates([]);
    setLoadingCountry('');
    syncLoadingCountryShow(false);
    syncCountryFieldKey(undefined);
    syncCountryFieldCodes([]);
  };

  const formatHTS = (input: string): string => {
    if (input.length === 0) {
      return '';
    }
    const digitsOnlyLocal = input.replace(/\D/g, '');

    if (digitsOnlyLocal.length !== 10) {
      enqueueSnackbar('Please enter a valid 10 digit HTS Code.', {
        variant: 'error',
      });
      return 'error';
    }
    const parts = [
      digitsOnlyLocal.slice(0, 4),
      digitsOnlyLocal.slice(4, 6),
      digitsOnlyLocal.slice(6, 8),
      digitsOnlyLocal.slice(8, 10),
    ];
    return parts.join('.');
  };

  // put near your other helpers
  const norm = (s: string) =>
    (s || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[\u2019’]/g, "'")
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const getIso2 = (c: any): string => {
    if (!c) {
      return '';
    }
    if (typeof c === 'string' && /^[A-Z]{2}$/i.test(c)) {
      return c.toUpperCase();
    }
    return (
      c.usCustomsCountryCode ??
      c.us_customs_country_code ??
      c.alpha2 ??
      c.alpha2Code ??
      c.iso2 ??
      c.country?.alpha2 ??
      c.country?.alpha2Code ??
      c.country?.iso2 ??
      ''
    );
  };

  const candidateHasCountryCond = (c: Candidate) =>
    Array.isArray(c.countryKeys) && c.countryKeys.length > 0;

  const findCountryFieldKeyOn = (cands: Candidate[]): string | undefined => {
    for (const c of cands) {
      if (Array.isArray(c.countryKeys) && c.countryKeys.length) {
        return c.countryKeys[0]; // first one wins (STEEL_COUNTRY_OF_POUR etc.)
      }
    }
    return undefined;
  };

  // helper: does this row still have a monetary effect?
  /**
   * Does this row actually charge anything?
   *
   * The engine stamps `_hasEffect` on every row it returns — it is the same
   * question the duty math already answered, and answering it here meant
   * keeping a copy of the rate parser for a rendering decision.
   *
   * Fail-open: only a row explicitly marked false is skipped. An unmarked row
   * (a locally built one, or an older saved snapshot) is treated as charging,
   * because the cost of over-showing a country input is far lower than the cost
   * of hiding one the user needs.
   */
  const rowHasPositiveEffect = (r: ResultRow): boolean =>
    (r as any)?._hasEffect !== false;

  const buildCountryUiState = (
    applied: Candidate[],
    resolvedLine1: ResultRow[],
    resolvedLine2: ResultRow[],
  ) => {
    const present = new Set<string>();
    for (const r of [...resolvedLine1, ...resolvedLine2]) {
      if (!rowHasPositiveEffect(r)) {
        continue;
      } // <-- ignore neutralized rows
      const d = digitsOnly(r._codeDigits || r.code || '');
      if (d) {
        present.add(d);
      }
    }

    const visibleApplied = applied.filter((c) => present.has(c.codeDigits));
    const countryApplied = visibleApplied.filter(candidateHasCountryCond);

    const codes = Array.from(new Set(countryApplied.map((c) => c.codePretty)));
    const key = findCountryFieldKeyOn(countryApplied);

    return { show: codes.length > 0, codes: codes, key: key };
  };

  // If base is Free, rewrite Base Duty rows to 'Free' so they contribute $0
  const applyFreeBaseToLines = (
    lines: Line[] = [],
    baseDutyRate: unknown,
  ): Line[] => {
    if (!isFreeBase(baseDutyRate)) {
      return lines;
    }
    return lines.map((l) => ({
      ...l,
      rows: l.rows.map((r) =>
        r.type === 'Base Duty' ? { ...r, rate: 'Free' } : r,
      ),
    }));
  };

  const getCountryName = (c: any): string => {
    if (!c) {
      return '';
    }
    if (typeof c === 'string') {
      return c;
    }

    // common shapes we’ve seen
    return (
      c.countryName ??
      c.name ??
      c.displayName ??
      c.label ??
      c.country?.name ??
      c.country?.countryName ??
      '' // fall through
    );
  };

  const ftaApplies = (entry: { countries: string[] }, origin: string) => {
    if (!entry || !entry.countries) {
      return false;
    }
    if (entry.countries.includes('Global')) {
      return true;
    }
    const o = norm(origin);
    return entry.countries.some((c) => norm(c) === o);
  };

  const extractSpecialCodes = (specialText: string): string[] => {
    if (!specialText) {
      return [];
    }
    const m = specialText.match(
      /Special\s+Rate\s+of\s+Duty:[^()]*\(([^)]*)\)/i,
    );
    const raw = m ? m[1] : '';
    const codes = raw
      .split(',')
      .map((s) => s.trim())
      .filter((s) => /^[A-Z]{1,2}[\+\*]?$/i.test(s));
    const compacted = codes.map((c) => c.replace(/\s+/g, ''));
    const valid = compacted.filter((c) =>
      Object.prototype.hasOwnProperty.call(FTA, c),
    );
    return [...new Set(valid)];
  };

  // Rebuild once more using the post-exclusion visible digits.
  // This unlocks codes like 9903.85.08 that were blocked by a code you just removed.
  /**
   * A toggle changed — recompute.
   *
   * This used to run a two-pass local candidate build and hand the result to
   * recomputeFromState. The engine rebuilds candidates from the claimed codes
   * itself, so there is nothing to pass: the toggle maps are the whole input.
   */
  const cascadeRebuild = (
    overrideFtas?: Record<string, boolean>,
    overrideExclusions?: Record<string, boolean>,
  ) => {
    recomputeFromState(
      undefined,
      overrideFtas ?? enabledFtas,
      overrideExclusions ?? enabledExclusions,
    );
  };

  const isColumn2Origin = (c: any): boolean => {
    // Prefer ISO-2 if you have it
    const iso = (getIso2(c) || '').toUpperCase();
    if (iso && COLUMN2_ISO2.has(iso)) {
      return true;
    }

    // Fallback on names (normalized)
    const n = norm(getCountryName(c));
    return [
      'belarus',
      'cuba',
      'north korea',
      "democratic people's republic of korea",
      'dprk',
      'russia',
      'russian federation',
    ].includes(n);
  };

  /* ---------- LIVE math helpers ---------- */

  // ---------- Units & parsing ----------

  // Located around line 1599 in your provided code

  /* -------------------- Candidate builder -------------------- */

  /**
   * Form state derived from an engine answer.
   *
   * Called from BOTH the first calculation and every toggle. Toggling an
   * exclusion changes which candidates apply, which can change whether a
   * country input is needed and whether the line splits — so this cannot live
   * in handleSubmit alone.
   */
  const applyEngineFormState = (remote: any) => {
    const engineResults = remote?.results || {};
    setTariffCandidates(engineResults.candidates || []);
    setImportBans(
      Array.isArray(engineResults.importBans) ? engineResults.importBans : [],
    );

    // buildCountryUiState is presentation over candidates, not duty math, so
    // it stays here and runs on the engine's candidate set.
    const countryUi = buildCountryUiState(
      engineResults.candidates || [],
      // This answer's rows. tariffResults still holds the previous answer
      // until the next render, so the field was labelled from the last check.
      engineResults.summaryRows || [],
      [],
    );
    const engineCountryCodes: string[] = engineResults.countryKeyCodes || [];
    const finalShow =
      countryUi.show ||
      engineCountryCodes.length > 0 ||
      Boolean(engineResults.needsLoadingCountry);
    const finalCodes =
      countryUi.codes && countryUi.codes.length
        ? countryUi.codes
        : engineCountryCodes;

    syncLoadingCountryShow(finalShow);
    syncCountryFieldKey(countryUi.key);
    syncCountryFieldCodes(finalCodes);

    if (finalShow && !loadingCountry) {
      setLoadingCountry(
        tariffInputs.originIso2 || tariffInputs.originCountry || '',
      );
    }

    // Seed split percentages without overwriting a user entry.
    const splitShares: any[] = engineResults.splitShares || [];
    if (splitShares.length > 0) {
      setMaterialPctByKey((prev) => {
        const next = { ...(prev ?? {}) };
        let added = false;
        for (const sh of splitShares) {
          if (sh?.key && next[sh.key] === undefined) {
            next[sh.key] = Number(sh.pct) || 0;
            added = true;
          }
        }
        // materialPctByKey re-posts on change, so a fresh object on every
        // answer would re-post forever.
        return added ? next : prev;
      });
    }

    syncLineSplitShow(splitShares.length > 0);
    syncAmmountShow((engineResults.requiredAmountUnits?.length || 0) > 0);
  };

  // Guards against an out-of-order server recompute; see runEngine.
  const engineSeqRef = React.useRef(0);
  // engineSeqRef only covers the engine call. A run that a newer one has
  // replaced must also stop after its lookups, or it reaches runEngine last
  // and supersedes the newer run with its own answer.
  const submitSeqRef = React.useRef(0);
  // Unmounting drops any run in flight, so its snackbars don't outlive it.
  useEffect(
    () => () => {
      submitSeqRef.current += 1;
    },
    [],
  );

  /**
   * Re-run the calculation on the server. Returns true when the caller may
   * skip its local pipeline. See utils/tariffEngine.
   */
  const recomputeViaEngine = (
    ftas: Record<string, boolean>,
    excls: Record<string, boolean>,
    logOnly: boolean,
  ): Promise<any | null> =>
    runEngine({
      inputs: buildEngineInputs(
        tariffInputs,
        productValue || 0,
        amountInputs,
        materialPctByKey,
        ftas,
        excls,
        loadingCountry,
        {
          entryDate: effectDate,
          loadingDate: loadDate,
          importBanAnswers: banGateAnswers,
        },
      ),
      current: tariffResults?.data || {},
      productValue: productValue || 0,
      seqRef: engineSeqRef,
      label: `${tariffInputs.htsCode} / ${tariffInputs.originCountry}`,
      logOnly: logOnly,
      setData: (data: any) => setTariffResults({ isLoading: false, data: data }),
    });

  const recomputeFromState = async (
    overrideCandidates?: Candidate[],
    overrideFtas?: Record<string, boolean>,
    overrideEnabledExclusions?: Record<string, boolean>,
  ) => {
    setTariffResults((prev: any) => ({ ...(prev || {}), isLoading: true }));

    const ftas: Record<string, boolean> = overrideFtas ?? enabledFtas;
    const excls: Record<string, boolean> =
      overrideEnabledExclusions ?? enabledExclusions;

    // --- Duty: recomputed by the shared engine ---------------------------
    // The ~490 lines that stood here re-ran the entire candidate build and
    // duty math locally on every toggle. The engine does that now, and a
    // toggle is a re-post — the exclusion map goes as `claimedCh99Codes` and
    // the FTA map as `spiCode` — rather than a second implementation of the
    // same pipeline.
    //
    // `overrideCandidates` is intentionally unused: the engine rebuilds the
    // candidate set from the claimed codes, which is the authoritative answer.
    const served = await recomputeViaEngine(ftas, excls, false);
    if (served === SUPERSEDED) {
      return;
    }
    if (!served) {
      enqueueSnackbar('Could not recalculate duty. Please try again.', {
        variant: 'error',
      });
      setTariffResults((prev: any) => ({ ...(prev || {}), isLoading: false }));
      return;
    }
    applyEngineFormState(served);
  };

  // FTA toggle
  const toggleFTA = (ftaKey: string, checked: boolean) => {
    const next = { ...enabledFtas, [ftaKey]: checked };
    if (enabledFtas[ftaKey] === checked) {
      return;
    }

    syncEnabledFtas(next);
    cascadeRebuild(next, undefined); // ftas overridden, exclusions unchanged
  };

  // Optional exclusion toggle
  const toggleExclusion = (codeDigits: string, enabled: boolean) => {
    const next = { ...enabledExclusions, [codeDigits]: enabled };
    if (enabledExclusions[codeDigits] === enabled) {
      return;
    }

    syncEnabledExclusions(next);
    cascadeRebuild(undefined, next); // ftas unchanged, exclusions overridden
  };

  // normalize helpers
  const digits10 = (s: string) =>
    String(s || '')
      .replace(/\D/g, '')
      .slice(0, 10);
  const originKey = (originIso2: string, originName: string) =>
    (originIso2?.toUpperCase() || '').trim() ||
    (originName || '').trim().toLowerCase();

  // track last fetched pair to avoid duplicate calls
  const lastFetchRef = React.useRef<{ hts: string; origin: string } | null>(
    null,
  );

  // The effects above copy the caller's inputs, amounts, material shares and
  // loading country into state, so they only land on the render after mount.
  // The auto-run waits for that render: its run prices with the state of the
  // render that scheduled it, before resetTransactionState clears the shares
  // and loading country for the next one.
  const [callerInputsSynced, setCallerInputsSynced] = useState(false);
  useEffect(() => {
    setCallerInputsSynced(true);
  }, []);

  // FULL REFRESH when HTS or Origin change
  useEffect(() => {
    const hts = digits10(tariffInputs.htsCode);
    const origin = originKey(
      tariffInputs.originIso2,
      tariffInputs.originCountry,
    );

    const ready = callerInputsSynced && hts.length === 10 && origin.length > 0;
    if (!ready) {
      return;
    }

    // The run scheduled below replaces any in flight, which would otherwise
    // show the previous code's results until it starts.
    submitSeqRef.current += 1;

    // 🚨 New: wipe prior toggles/options before we build a fresh transaction
    resetTransactionState();
    setTariffResults({ isLoading: true, data: {} });
    setAdcvdResults('');
    setHtsDocument({});
    lastFetchRef.current = null;

    const id = setTimeout(async () => {
      const hAgain = digits10(tariffInputs.htsCode);
      const oAgain = originKey(
        tariffInputs.originIso2,
        tariffInputs.originCountry,
      );
      // if (hAgain.length !== 10 || !oAgain) {
      //   return;
      // }

      lastFetchRef.current = { hts: hAgain, origin: oAgain };
      await handleSubmit();
    }, 400);

    return () => clearTimeout(id);
  }, [
    tariffInputs.htsCode,
    tariffInputs.originCountry,
    tariffInputs.originIso2,
    callerInputsSynced,
  ]);

  useEffect(() => {
    if (
      !tariffResults?.data?.baseDutyRateRaw &&
      !tariffResults?.data?.activeImportBan
    ) {
      return;
    }
    if (!effectDate || !loadDate) {
      return;
    }
    if (
      digits10(tariffInputs.htsCode).length !== 10 ||
      !originKey(tariffInputs.originIso2, tariffInputs.originCountry)
    ) {
      return;
    }

    const id = setTimeout(() => {
      lastFetchRef.current = null;
      handleSubmit();
    }, 250);

    return () => clearTimeout(id);
  }, [effectDate?.getTime?.(), loadDate?.getTime?.()]);

  useEffect(() => {
    // need a base rate to be present (i.e., after first handleSubmit)
    if (!tariffResults?.data?.baseDutyRateRaw) {
      return;
    }

    // The loading country gates rule applicability, so a change re-posts. The
    // engine rebuilds the candidate set from the claimed codes and
    // applyEngineFormState publishes it, so there is nothing to build here.
    recomputeFromState(undefined, enabledFtas);
  }, [loadingCountry]); // 👈 critical

  const countryMatches = (
    countryA: string | null | undefined,
    countryB: string | null | undefined,
  ): boolean => {
    const normA = normalizeCountry(countryA);
    const normB = normalizeCountry(countryB);

    if (!normA || !normB) {
      return false;
    }

    // "Global" matches everything
    if (normA === 'global' || normB === 'global') {
      return true;
    }

    return normA === normB;
  };

  /* -------------------- Submit -------------------- */
  const handleSubmit = async () => {
    if (isStaticView) {
      return;
    }
    const val = formatHTS(tariffInputs.htsCode);
    if (val === 'error') {
      return;
    }

    const requestId = ++submitSeqRef.current;
    const isStale = () => requestId !== submitSeqRef.current;

    // New transaction → ensure no stale toggles sneak in
    resetTransactionState();

    hydratingRef.current = true;
    setLoading(true);
    setTariffResults({ isLoading: true, data: {} });
    setAdcvdResults('');
    setHtsDocument({});

    try {
      // 1) Fetch the HTS record
      const res = await DatahubAPI.retrieveHTSCode({
        code: val,
        country: tariffInputs.originCountry,
      });
      if (isStale()) return;

      const hits = res?.result?.result?.body?.hits?.hits;
      if (!Array.isArray(hits)) {
        enqueueSnackbar(
          'Unexpected response while retrieving tariffs. Please try again.',
          {
            variant: 'error',
          },
        );
        setTariffResults({ isLoading: false, data: {} });
        setLoading(false);
        hydratingRef.current = false;
        return;
      }

      if (hits.length === 0) {
        // The legacy HTS metadata index does not contain every jurisdiction.
        // Do not let a missing metadata record hide an import ban: the shared
        // tariff engine is the authoritative calculation source and can still
        // return a barred result plus its applicability question.
        const remote = await recomputeViaEngine(
          enabledFtas,
          enabledExclusions,
          false,
        );
        if (remote === SUPERSEDED || isStale()) return;
        if (remote) {
          applyEngineFormState(remote);
          setLoading(false);
          hydratingRef.current = false;
          return;
        }

        enqueueSnackbar(
          `No tariff record found for HTS ${val}${
            tariffInputs.originCountry
              ? ` (origin: ${tariffInputs.originCountry})`
              : ''
          }.`,
          { variant: 'warning' },
        );
        setTariffResults({ isLoading: false, data: {} });
        setLoading(false);
        hydratingRef.current = false;
        return;
      }

      // --- UPDATED HISTORY LOGIC START ---
      if (Array.isArray(hits)) {
        const today = new Date();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(today.getDate() - 30);
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(today.getDate() + 30);

        // 1. Sort Descending (Newest first)
        const sortedHits = hits
          .map((h: any, realIndex: number) => {
            const src = h._source;
            return {
              id: h._id || `hit-${realIndex}`,
              rate: src['General Duty Rate'],
              effFrom: src['Effective From']
                ? new Date(src['Effective From'])
                : null,
              effTo: src['Effective To'] ? new Date(src['Effective To']) : null,
            };
          })
          .sort((a, b) => {
            const tA = a.effFrom ? a.effFrom.getTime() : 0;
            const tB = b.effFrom ? b.effFrom.getTime() : 0;
            return tB - tA; // Descending
          });

        const historyNotifications: HtsHistoryItem[] = [];

        sortedHits.forEach((hit, index) => {
          const { rate, effFrom, effTo } = hit;

          // Safety check: skip missing dates OR ancient "epoch" dates (e.g. 1969/1970)
          // valid dates usually are > year 2000 for this context
          if (!effFrom || effFrom.getFullYear() < 2000) return;

          // A. Recently Added or Updated (< 30 Days Ago)
          if (effFrom >= thirtyDaysAgo && effFrom <= today) {
            // Look ahead for the chronologically previous record
            const previousRecord = sortedHits[index + 1];

            let summaryText = '';

            if (previousRecord) {
              const oldRate = previousRecord.rate;
              if (oldRate !== rate) {
                summaryText = `HTS code updated — duty rate changed from ${oldRate} to ${rate}.`;
              } else {
                summaryText = `HTS code updated — duty rate remains ${rate}.`;
              }
            } else {
              // No previous record = Recently Added
              summaryText = `HTS code added to tariff schedule with a rate of ${rate}.`;
            }

            historyNotifications.push({
              id: `update-${index}`,
              date: format(effFrom, 'MMM d, yyyy'),
              summary: summaryText,
            });
          }

          // B. Expiring Soon (Next 30 Days)
          if (effTo && effTo <= thirtyDaysFromNow && effTo >= today) {
            historyNotifications.push({
              id: `expire-${index}`,
              date: format(effTo, 'MMM d, yyyy'),
              summary: `This HTS provision is scheduled to expire. Ensure your classification remains valid for future entries.`,
            });
          }

          // REMOVED: Block C (Historic) is gone.
          // You will now only see items that fit the strict 30-day window above.
        });

        setHtsHistory(historyNotifications);
      }
      // --- UPDATED HISTORY LOGIC END ---

      // --- DATE-BASED DOCUMENT SELECTION LOGIC ---
      const entryDateStr = (() => {
        if (!effectDate || Number.isNaN(effectDate.getTime())) {
          return '0000-00-00';
        }

        // Use the date-fns library (which you already have imported!)
        // to grab the local calendar date safely as a string.
        return format(effectDate, 'yyyy-MM-dd');
      })();

      const matchedHit = hits.find((h: any) => {
        const src = h._source || {};

        // 1. Effective From (Default: Start of time)
        let fromStr = '0000-00-00';
        if (src['Effective From']) {
          fromStr = String(src['Effective From']).slice(0, 10);
        }

        // 2. Effective To (Default: End of time)
        let toStr = '9999-12-31';
        if (src['Effective To']) {
          toStr = String(src['Effective To']).slice(0, 10);
        }

        // Check if Entry Date falls within the document's validity window [Inclusive]
        return entryDateStr >= fromStr && entryDateStr <= toStr;
      });

      if (!matchedHit) {
        enqueueSnackbar(
          `No valid tariff record found for HTS ${val} on ${entryDateStr}.`,
          {
            variant: 'warning',
          },
        );
        setTariffResults({ isLoading: false, data: {} });
        setLoading(false);
        hydratingRef.current = false;
        return;
      }

      const htsDoc = matchedHit._source;

      // --- NEW: Prune "Archived" words from the code before use ---
      if (htsDoc && htsDoc['HS Code']) {
        htsDoc['HS Code'] = String(htsDoc['HS Code']).split(' - ')[0].trim();
      }
      // -----------------------------------------------------------

      if (!htsDoc) {
        enqueueSnackbar(
          'Tariff record was missing expected fields. Please try again.',
          {
            variant: 'error',
          },
        );
        setTariffResults({ isLoading: false, data: {} });
        setLoading(false);
        hydratingRef.current = false;
        return;
      }
      setHtsDocument(htsDoc);

      // AD/CVD + Forced Labor are matched by their exact `List Name`. PGA
      // records are named "PGA Codes - <Agency>" (FDA, EPA, USDA, …), so they
      // never equal a `List Name` of 'PGA Codes' and can only be matched by
      // `BITE Lens` === 'PGA Codes'. The datahub commodity handler ANDs the
      // lens + listName filters together, so PGA needs its own query; its hits
      // stay separate (pgaHits) so they can't trip the AD/CVD country check.
      const baseFlagQuery = {
        indices: ['bite_app_commodities_*'],
        primary: val,
        type: 'import',
        agg: true,
      };
      const [adcvdRes, pgaRes] = await Promise.all([
        DatahubAPI.commoditySearch({
          ...baseFlagQuery,
          listName: [
            'U.S. Anti Dumping Countervailing Duty Orders (by Order)',
            'Forced and Child Labor HS Codes',
          ],
        }),
        DatahubAPI.commoditySearch({ ...baseFlagQuery, lens: ['PGA Codes'] }),
      ]);
      if (isStale()) return;

      if (adcvdRes?.success) {
        const adcvdHits = adcvdRes?.result?.result?.body?.hits?.hits;

        if (!Array.isArray(adcvdHits)) {
          enqueueSnackbar(
            'Unexpected response while retrieving tariffs. Please try again.',
          );
        } else {
          // --- NEW: Risk Flag Processing ---
          const newFlags: RiskFlag[] = [];
          const origin = tariffInputs.originCountry;

          // 1. AD/CVD Check (Robust Method)
          // We use the exact same logic as the old processHits function:
          // Check ALL hits for a matching Risk Country, regardless of List Name.
          const hasAdCvdRisk = adcvdHits.some((h: any) =>
            countryMatches(origin, h._source?.['BITE-Identified Risk Country']),
          );

          if (hasAdCvdRisk) {
            newFlags.push({
              id: 'adcvd',
              title: 'Potential AD/CVD',
              description:
                'This transaction may be subject to AD / CVD orders. For more detailed information visit the BITE Import Screening Tool and search your transaction details utilizing the AD CVD Commodities filter.',
              severity: 'high',
            });
          }

          // 2. Forced Labor Check
          const flHits = adcvdHits.filter(
            (h: any) =>
              h._source?.['List Name'] === 'Forced and Child Labor HS Codes',
          );
          if (flHits.length > 0) {
            newFlags.push({
              id: 'forced-labor',
              title: 'Forced Labor',
              description:
                'This product or origin may be flagged under UFLPA or WRO enforcement.',
              severity: 'medium',
            });
          }

          // 3. PGA Check (PGA records come from the dedicated BITE Lens query)
          const pgaHits = Array.isArray(
            pgaRes?.result?.result?.body?.hits?.hits,
          )
            ? pgaRes.result.result.body.hits.hits
            : [];
          if (pgaHits.length > 0) {
            newFlags.push({
              id: 'pga',
              title: 'PGA Requirement',
              description:
                'Partner Government Agency filing may be required for this entry.',
              severity: 'info',
            });
          }

          setRiskFlags(newFlags);

          // REMOVED: setAdcvdResults(adcvdResult);
          // We no longer set the old state, so the old box won't trigger.
        }
      }
      const originForColumn2 =
        tariffInputs.originIso2 || tariffInputs.originCountry;

      const generalRate = htsDoc['General Duty Rate'];
      const column2Rate = htsDoc['Column 2 Duty Rate'];

      // Use Column 2 when origin is Belarus, Cuba, North Korea, or Russia (BY, CU, KP, RU)
      const useColumn2 =
        isColumn2Origin(originForColumn2) &&
        String(column2Rate || '').trim() !== '';

      const baseDuty = useColumn2 ? column2Rate : generalRate;
      const desc = htsDoc['Product Name'];

      const baseRow: ResultRow = {
        type: ['BY', 'CU', 'KP', 'RU'].includes(tariffInputs.originIso2)
          ? 'Base Duty (Column 2)'
          : 'Base Duty',
        code: htsDoc['HS Code'], // Now using the cleaned code
        rate: baseDuty,
      };

      const dataObj: any = {
        baseDutyRate: baseDuty,
        baseDutyRateRaw: baseDuty,
        additionalDutyRate: '0%',
        productDesc: desc,
        results: [baseRow],
        lines: [],
        lineSplitLabel: '',
        optionalExclusions: [],
      };

      // 2) FTA options (seed toggles OFF)
      const specialField =
        htsDoc['Special Duty Rate'] || htsDoc['Special Rate of Duty'] || '';
      const specialCodes = extractSpecialCodes(String(specialField));

      const ftaOptions: FtaOption[] = [];
      const initialEnabledFtas: Record<string, boolean> = {};

      const originLocal = (tariffInputs.originCountry || '')
        .trim()
        .toLowerCase();
      const baseRateIsFree = isFreeBase(baseDuty);

      // --- 2a. New generalized FTA injection when base rate is FREE -----------------
      const MANUAL_FTA_BY_COUNTRY: Record<string, string> = {
        canada: 'S',
        mexico: 'S',
        'costa rica': 'P',
        'dominican republic': 'P',
        'el salvador': 'P',
        guatemala: 'P',
        honduras: 'P',
        nicaragua: 'P',
        chile: 'CL',
        israel: 'IL',
        isreal: 'IL',
        jordan: 'JO',
        japan: 'JP',
        morocco: 'MA',
        singapore: 'SG',
        oman: 'OM',
        peru: 'PE',
        'south korea': 'KR',
        'republic of korea': 'KR',
        'korea, republic of': 'KR',
        colombia: 'CO',
        panama: 'PA',
        nepal: 'NP',
      };

      const findManualFtaCode = (countryLower: string): string | null => {
        for (const key of Object.keys(MANUAL_FTA_BY_COUNTRY)) {
          if (countryLower.includes(key)) {
            return MANUAL_FTA_BY_COUNTRY[key];
          }
        }
        return null;
      };

      const manualFtaCode = findManualFtaCode(originLocal);

      if (baseRateIsFree && manualFtaCode) {
        const entry = FTA[manualFtaCode];
        if (entry) {
          const alreadyInSpecial = specialCodes.includes(manualFtaCode);
          if (!alreadyInSpecial) {
            ftaOptions.push({ code: manualFtaCode, name: entry.name });
          }
          initialEnabledFtas[manualFtaCode] = false;
        }
      }

      // --- 2b. Process existing HTS special rates ---
      if (specialCodes.length) {
        const origin = tariffInputs.originCountry;
        for (const code of specialCodes) {
          const entry = FTA[code];
          if (manualFtaCode && code === manualFtaCode && baseRateIsFree) {
            continue;
          }

          if (entry && ftaApplies(entry, origin)) {
            ftaOptions.push({ code: code, name: entry.name });
            initialEnabledFtas[code] = false;
          }
        }
      }

      const uniqueFtaOptions = Array.from(
        new Map(ftaOptions.map((item) => [item.code, item])).values(),
      );

      syncEnabledFtas(initialEnabledFtas);
      dataObj.baseDutyRateRaw = baseDuty;
      dataObj.baseDutyRate = baseDuty;
      dataObj.ftaOptions = uniqueFtaOptions;

      // 3) Duty — computed by the shared engine ------------------------------
      // What stood here was ~340 lines: the candidate build, dependency
      // expansion, multi-line assignment, IEEPA/Pharma/Russia normalization,
      // summary dedupe, split seeding, display lines, the optional-exclusion
      // pool and the duty math. That is the engine, and the engine now runs in
      // one place — biteservices services/shared/src, the same code the ACE
      // Analyzer runs. What remains is mapping its answer onto this form.
      //
      // There is no local fallback here any more: the pipeline this would fall
      // back TO is what was removed. A server failure is an error, handled the
      // same way a failed HTS lookup is.
      const remote = await runEngine({
        inputs: buildEngineInputs(
          tariffInputs,
          productValue || 0,
          amountInputs,
          materialPctByKey,
          undefined,
          undefined,
          loadingCountry,
          { entryDate: effectDate, loadingDate: loadDate },
        ),
        current: dataObj,
        productValue: productValue || 0,
        seqRef: engineSeqRef,
        label: `${tariffInputs.htsCode} / ${tariffInputs.originCountry}`,
        logOnly: false,
        setData: (data: any) => Object.assign(dataObj, data),
      });

      if (remote === SUPERSEDED) {
        // A later calculation owns the render.
        return;
      }
      if (!remote) {
        enqueueSnackbar(
          'Could not calculate duty for this line. Please try again.',
          { variant: 'error' },
        );
        setTariffResults({ isLoading: false, data: {} });
        setLoading(false);
        hydratingRef.current = false;
        return;
      }

      applyEngineFormState(remote);

      setTariffResults({ isLoading: false, data: dataObj });
      setLoading(false);
      hydratingRef.current = false;
    } catch (e) {
      if (isStale()) return;
      enqueueSnackbar(
        `Error retrieving tariffs. Please try again. ERROR: ${e}`,
        {
          variant: 'error',
        },
      );
      setTariffResults({ isLoading: false, data: {} });
      setLoading(false);
      hydratingRef.current = false;
    }
  };

  // ---- Live totals shown in the right-hand card ----
  // The engine's own figures. These used to be re-derived here from the lines
  // and the base rate — a second implementation of the duty math for a number
  // the engine already reported, and one the two could disagree on.
  const {
    additional: additionalDutyDollars,
    baseAmount: baseAmountDollars,
    liveAdditionalRate,
  } = React.useMemo(() => {
    const em = tariffResults.data?.engineMath || {};
    const additional = Number(em.additionalDutyDollars) || 0;
    const baseAmount = Number(em.baseAmountDollars) || 0;
    const pct = productValue ? (additional / productValue) * 100 : 0;
    return {
      additional: additional,
      baseAmount: baseAmount,
      liveAdditionalRate: `${pct
        .toFixed(2)
        .replace(/\.00$/, '')
        .replace(/(\.\d)0$/, '$1')}%`,
    };
  }, [tariffResults.data?.engineMath, productValue]);

  // Base % dollars — the ad-valorem portion, backed out of the engine's total
  // rather than recomputed per line.
  const basePercentDutyDollars = React.useMemo(() => {
    const em = tariffResults.data?.engineMath || {};
    const total = Number(em.dutyCost) || 0;
    const additional = Number(em.additionalDutyDollars) || 0;
    const baseAmount = Number(em.baseAmountDollars) || 0;
    return Math.max(0, total - additional - baseAmount);
  }, [tariffResults.data?.engineMath]);

  // ✅ Final totals used by <TarifFResultsComp />
  // ✅ Final totals used by <TarifFResultsComp />
  const dutyCost = React.useMemo(
    () => additionalDutyDollars + baseAmountDollars + basePercentDutyDollars,
    [additionalDutyDollars, baseAmountDollars, basePercentDutyDollars],
  );

  // MPF, HMF and the total are the engine's: it applies the fee's fiscal-year
  // bounds, the Chapter 98 and origin exemptions, and the FTA exemptions for
  // the SPIs the toggles posted.
  const { mpf, hmf, totalCost } = React.useMemo(
    () => engineFigures(tariffResults.data?.engineMath),
    [tariffResults.data?.engineMath],
  );

  const aiExplain = async () => {
    try {
      setAILoading(true);

      const chapter99Codes = Array.from(
        new Set(
          (tariffResults.data.results as ResultRow[])
            .map((r) => r?.code || '')
            .filter(Boolean)
            .map((code) => formatHtsPretty(String(code)))
            .filter((pretty) => /^\s*99/.test(pretty.replace(/\D/g, ''))),
        ),
      );

      const otherTariffLayers = (tariffResults.data.results as ResultRow[])
        .filter(
          (r) => r.type !== 'Base Duty' && r.type !== 'Base Duty (Column 2)',
        )
        .map((r) => ({ code: r.code, rate: r.rate, type: r.type }));

      const explainReq = {
        hs_code: formatHtsPretty(
          htsDocument['HS Code'] || formatHTS(tariffInputs.htsCode),
        ),
        country: tariffInputs.originCountry,
        transaction_date: format(effectDate, 'yyyy-MM-dd'),
        general_rate:
          tariffResults.data.baseDutyRateRaw ??
          tariffResults.data.baseDutyRate ??
          '',
        column2_rate: `Not Applicable - ${tariffInputs.originCountry} is not subject to Column 2 duties`,
        special_duty_rate: String(
          (
            htsDocument['Special Duty Rate'] ||
            htsDocument['Special Rate of Duty'] ||
            ''
          ).trim() || 'No Special Duty Found',
        ),
        ad_cvd_status:
          'This transaction may not be subject to AD / CVD orders.',
        chapter99_codes: chapter99Codes,
        other_tariff_layers: otherTariffLayers,
      } as const;

      const aiResp = await runAIExplanationOnly(explainReq);
      setExplanationText(aiResp?.explanation ?? '');
      if (!aiResp?.explanation) {
        enqueueSnackbar(
          'Unable to generate an explanation. Please try again.',
          {
            variant: 'error',
          },
        );
      }
      setAILoading(false);
    } catch (e) {
      console.warn('[AI Explanation] Failed to fetch explanation', e);
      setAILoading(false);
    }
  };

  // 3. GENERATE QUESTIONS FROM CANDIDATES
  // 3. GENERATE QUESTIONS FROM CANDIDATES
  const exclusionQuestions = React.useMemo(() => {
    // CHANGED: Source from the calculated results (displayed in table) instead of raw candidates.
    const activeCandidates =
      (tariffResults?.data?.optionalExclusions as Candidate[]) || [];

    const groups: Record<
      string,
      { id: string; label: string; enablesExclusions: string[] }[]
    > = {};

    activeCandidates.forEach((c) => {
      const q = c.bd_232_question;
      const a = c.bd_232_answer;

      if (q && a) {
        if (!groups[q]) {
          groups[q] = [];
        }

        const answerLabel = a.charAt(0).toUpperCase() + a.slice(1); // Capitalize "yes" -> "Yes"
        const answerId = a.toLowerCase();

        // Check if we already have this answer option for this question
        let option = groups[q].find((o) => o.id === answerId);

        if (!option) {
          option = {
            id: answerId,
            label: answerLabel,
            enablesExclusions: [],
          };
          groups[q].push(option);
        }

        // Add this candidate's code to the option's enablement list
        if (!option.enablesExclusions.includes(c.codeDigits)) {
          option.enablesExclusions.push(c.codeDigits);
        }
      }
    });

    // Convert map to array for standard dynamic questions
    const generatedQuestions = Object.entries(groups).map(
      ([prompt, options], idx) => ({
        id: `generated-q-${idx}`,
        prompt: prompt,
        helperText:
          'Select the option that describes your product to apply the correct tariff or exclusion.',
        options: options.sort((a, b) => {
          // Simple sort to put "Yes" before "No" usually, or alphabetical
          if (a.id === 'yes') return -1;
          if (b.id === 'yes') return 1;
          return a.label.localeCompare(b.label);
        }),
      }),
    );

    // --- HARDCODED OVERRIDE LOGIC START ---
    const TARGET_PROMPT =
      'Is it an auto part or heavy duty vehicle part or neither?';

    // We only inject this question if the relevant codes are actually present
    // in the current transaction candidates (to avoid showing it on unrelated products).
    const hasRelevantCodes = activeCandidates.some(
      (c) => c.codeDigits === '99037411' || c.codeDigits === '99039406',
    );

    if (hasRelevantCodes) {
      // 1. Filter out the auto-generated version if it exists (to avoid duplicates)
      const filtered = generatedQuestions.filter(
        (q) => q.prompt !== TARGET_PROMPT,
      );

      // 2. Define the hardcoded question object
      const hardcodedQuestion = {
        id: 'manual-232-autopart',
        prompt: TARGET_PROMPT,
        helperText:
          'Select the option that describes your product to apply the correct tariff or exclusion.',
        options: [
          {
            id: 'auto_part',
            label: 'Auto Part',
            enablesExclusions: ['99037411'], // 1. Auto Part -> 99037411
          },
          {
            id: 'heavy_duty',
            label: 'Heavy Duty Vehicle Part',
            enablesExclusions: [], // 2. Heavy Duty -> None
          },
          {
            id: 'neither',
            label: 'Neither',
            enablesExclusions: ['99037411', '99039406'], // 3. Neither -> Both
          },
        ],
      };

      // 3. Append to list
      return [...filtered, hardcodedQuestion];
    }
    // --- HARDCODED OVERRIDE LOGIC END ---

    return generatedQuestions;
  }, [tariffResults?.data?.optionalExclusions]); // CHANGED: Dependency matches data source

  const importBanQuestions = React.useMemo(() => {
    const questions: Array<{
      id: string;
      prompt: string;
      helperText: string;
      options: Array<{ id: string; label: string }>;
      selected?: string;
    }> = [];
    importBans.forEach((ban) => {
      ban.gates.forEach((gate) => {
        if (gate.keepOnAnswer === null || questions.some((q) => q.id === gate.id)) {
          return;
        }
        questions.push({
          id: gate.id,
          prompt: gate.question,
          helperText: `${ban.measure}. The goods are excluded from importation unless this answer lifts the ban.`,
          options: [
            { id: 'yes', label: 'Yes' },
            { id: 'no', label: 'No' },
          ],
          selected: importBanGateAnswer(gate, banGateAnswers) ?? undefined,
        });
      });
    });
    return questions;
  }, [importBans, banGateAnswers]);

  const handleImportBanAnswer = (gateId: string, optionId: string) => {
    if (isStaticView || (optionId !== 'yes' && optionId !== 'no')) {
      return;
    }
    const answer: BanGateAnswer = optionId;
    setBanGateAnswers((prev) => ({ ...prev, [gateId]: answer }));
  };

  // 4. HANDLE ANSWER SELECTION (BULK TOGGLE)
  const handleQuestionAnswer = (
    codesToEnable: string[],
    codesToDisable: string[],
  ) => {
    if (isStaticView) return;

    const next = { ...enabledExclusions };

    // First disable the "other" options
    codesToDisable.forEach((code) => {
      next[code] = false;
    });

    // Then enable the "selected" options
    codesToEnable.forEach((code) => {
      next[code] = true;
    });

    const keys = new Set([
      ...Object.keys(enabledExclusions),
      ...Object.keys(next),
    ]);
    const changed = Array.from(keys).some(
      (key) => Boolean(enabledExclusions[key]) !== Boolean(next[key]),
    );

    if (!changed) {
      return;
    }

    syncEnabledExclusions(next);
    cascadeRebuild(undefined, next);
  };

  useEffect(() => {
    if (
      !tariffResults?.data?.baseDutyRateRaw &&
      !tariffResults?.data?.activeImportBan
    ) {
      return;
    }
    if (loading || hydratingRef.current) {
      return;
    }

    // Duty and fees both come from the engine, so any input it prices on
    // re-posts — including on a line with no Chapter 99 candidates.
    recomputeFromState();
  }, [
    productValue,
    materialPctByKey,
    amountInputs,
    tariffInputs.transportMethod,
    banGateAnswers,
  ]);

  useEffect(() => {
    const all = (tariffResults?.data?.ftaAllCodes || []) as string[];
    if (!all.length) {
      return;
    }

    const freshOptions: FtaOption[] = [];
    const nextEnabled: Record<string, boolean> = {};

    for (const code of all) {
      const entry = FTA[code];
      if (entry && ftaApplies(entry, tariffInputs.originCountry)) {
        freshOptions.push({ code: code, name: entry.name });
        nextEnabled[code] = enabledFtas[code] ?? false; // keep toggle if same key existed
      }
    }

    setTariffResults((prev: any) => ({
      ...prev,
      data: { ...prev.data, ftaOptions: freshOptions },
    }));

    // Drop any toggles that are no longer applicable to the new origin
    const cleaned: Record<string, boolean> = {};
    for (const { code } of freshOptions) {
      cleaned[code] = Boolean(nextEnabled[code]);
    }
    syncEnabledFtas(cleaned);

    // origin affects candidate country filters too
    if (tariffResults?.data?.baseDutyRateRaw) {
      recomputeFromState();
    }
  }, [tariffInputs.originCountry, tariffInputs.originIso2]);

  useEffect(() => {
    const units: string[] | undefined =
      tariffResults?.data?.requiredAmountUnits;
    if (Array.isArray(units) && units.length > 0) {
      setAmountInputs((prev) => {
        const next = { ...prev };
        let changed = false;
        const normLoc = (u: string) =>
          u
            .trim()
            .toLowerCase()
            .replace(/\.$/, '')
            .replace(/²/g, '2')
            .replace(/³/g, '3')
            .replace(/\^2/g, '2')
            .replace(/\^3/g, '3')
            .replace(/\s+/g, '');

        for (const u of units) {
          const key = normLoc(u);
          if (next[key] === null || next[key] === undefined) {
            next[key] = 10; // default only if missing
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }
  }, [tariffResults?.data?.requiredAmountUnits]);

  useEffect(() => {
    // Only run after we have an initial result
    if (!tariffResults?.data?.baseDutyRateRaw) {
      return;
    }
    // Don’t run while the main request is still loading / hydrating
    if (loading || hydratingRef.current) {
      return;
    }
    // If the extra selector isn’t even shown, don’t bother
    if (!loadingCountryShow) {
      return;
    }
    // If there’s no chosen country yet, nothing to recompute
    if (!loadingCountry) {
      return;
    }

    // This uses the CURRENT ftas & exclusions and the NEW loadingCountry:
    // Pass 1 + Pass 2 in cascadeRebuild will rebuild candidates with the
    // new country, and recomputeFromState will rebuild the lines.
    cascadeRebuild();
  }, [loadingCountry]);

  return (
    <>
      <div className={`classify-res-comp ${isSelected ? 'selected' : ''}`}>
        <div className='classify-res-comp-header'>
          <div className='classify-res-comp-header-left-icon'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
            >
              <path
                d='M4 10.6666C4 12.1393 5.19391 13.3333 6.66667 13.3333H9.33333C10.8061 13.3333 12 12.1393 12 10.6666C12 9.19383 10.8061 7.99992 9.33333 7.99992H6.66667C5.19391 7.99992 4 6.80601 4 5.33325C4 3.86049 5.19391 2.66659 6.66667 2.66659H9.33333C10.8061 2.66659 12 3.86049 12 5.33325M8 1.33325V14.6666'
                stroke='var(--text-classify)'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
          <div className='classify-res-comp-header-text'>Tariffs & Duties</div>
          <div
            className='classify-res-comp-header-left-icon'
            style={{ paddingTop: '2px' }}
          >
            {Object.keys(tariffResults.data).length > 0 && !isExpand ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
              >
                <path
                  d='M13.3327 4L5.99935 11.3333L2.66602 8'
                  stroke='#17B26A'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ) : Object.keys(tariffResults.data).length > 0 && isExpand ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
              >
                <path
                  d='M13.3327 4L5.99935 11.3333L2.66602 8'
                  stroke='#17B26A'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
              >
                <g clipPath='url(#clip0_1_34184)'>
                  <path
                    d='M8.00016 3.99992V7.99992L10.6668 9.33325M14.6668 7.99992C14.6668 11.6818 11.6821 14.6666 8.00016 14.6666C4.31826 14.6666 1.3335 11.6818 1.3335 7.99992C1.3335 4.31802 4.31826 1.33325 8.00016 1.33325C11.6821 1.33325 14.6668 4.31802 14.6668 7.99992Z'
                    stroke='#F79009'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </g>
                <defs>
                  <clipPath id='clip0_1_34184'>
                    <rect width='16' height='16' fill='white' />
                  </clipPath>
                </defs>
              </svg>
            )}
          </div>
          <div
            className='classify-res-comp-header-left-icon'
            style={{ paddingTop: '2px' }}
          >
            {!isExpand ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                onClick={() => setIsExpand(!isExpand)}
              >
                <path
                  d='M10 12L6 8L10 4'
                  stroke='var(--text-classify)'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                onClick={() => setIsExpand(!isExpand)}
              >
                <path
                  d='M4 6L8 10L12 6'
                  stroke='var(--text-classify)'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            )}
          </div>
        </div>

        {!isExpand && !loading ? (
          Object.keys(tariffResults.data).length > 0 ? (
            <CostSummaryComp
              tariffResults={tariffResults.data}
              productValue={productValue}
              dutyCost={dutyCost}
              totalCost={totalCost}
              baseCost={baseAmountDollars + basePercentDutyDollars}
              displayAdditionalRate={liveAdditionalRate}
              tariffCandidates={tariffResults.data?.optionalExclusions ?? []}
              enabledExclusions={enabledExclusions}
              toggleExclusion={toggleExclusion}
              materialPct={tariffInputs.materialPct}
              ftaOptions={tariffResults.data?.ftaOptions ?? []}
              enabledFtas={enabledFtas}
              toggleFta={toggleFTA}
              explanationText={explanationText}
              aiLoading={aiLoading}
            />
          ) : (
            <p className='classification-status'>{'Awaiting information...'}</p>
          )
        ) : loading ? (
          <LoadingFeedback size='small' />
        ) : (
          Object.keys(tariffResults.data).length > 0 && (
            <>
              {!isExpand && (
                <CostSummaryComp
                  tariffResults={tariffResults.data}
                  productValue={productValue}
                  dutyCost={dutyCost}
                  totalCost={totalCost}
                  baseCost={baseAmountDollars + basePercentDutyDollars}
                  displayAdditionalRate={liveAdditionalRate}
                  tariffCandidates={
                    tariffResults.data?.optionalExclusions ?? []
                  }
                  enabledExclusions={enabledExclusions}
                  toggleExclusion={toggleExclusion}
                  materialPct={tariffInputs.materialPct}
                  ftaOptions={tariffResults.data?.ftaOptions ?? []}
                  enabledFtas={enabledFtas}
                  toggleFta={toggleFTA}
                  explanationText={explanationText}
                  aiLoading={aiLoading}
                />
              )}

              <div className='classify-res-comp-sec-col'>
                <div className='classify-res-comp-sec'>
                  <div className='classify-res-comp-sec-left'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                    >
                      <path
                        d='M13.6666 4.85173L7.99998 7.99987M7.99998 7.99987L2.33331 4.85173M7.99998 7.99987L8 14.3332M14 10.7056V5.29419C14 5.06576 14 4.95155 13.9663 4.84968C13.9366 4.75956 13.8879 4.67684 13.8236 4.60705C13.7509 4.52816 13.651 4.47269 13.4514 4.36176L8.51802 1.62102C8.32895 1.51598 8.23442 1.46346 8.1343 1.44287C8.0457 1.42464 7.95431 1.42464 7.8657 1.44287C7.76559 1.46346 7.67105 1.51598 7.48198 1.62102L2.54865 4.36176C2.34896 4.47269 2.24912 4.52816 2.17642 4.60705C2.11211 4.67684 2.06343 4.75957 2.03366 4.84968C2 4.95155 2 5.06576 2 5.29419V10.7056C2 10.934 2 11.0482 2.03366 11.1501C2.06343 11.2402 2.11211 11.3229 2.17642 11.3927C2.24912 11.4716 2.34897 11.5271 2.54865 11.638L7.48198 14.3788C7.67105 14.4838 7.76559 14.5363 7.8657 14.5569C7.95431 14.5751 8.0457 14.5751 8.1343 14.5569C8.23442 14.5363 8.32895 14.4838 8.51802 14.3788L13.4514 11.638C13.651 11.5271 13.7509 11.4716 13.8236 11.3927C13.8879 11.3229 13.9366 11.2402 13.9663 11.1501C14 11.0482 14 10.934 14 10.7056Z'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M11 6.33333L5 3'
                        stroke='var(--text-classify)'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </div>
                  <div className='classify-res-comp-sec-txt'>
                    Exclusion Applicability Assistant, Risk Flags and HTS Code
                    History
                  </div>
                  <div className='classify-res-comp-sec-left'>
                    {!isExpandFlags ? (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                        onClick={() => setIsExpandFlags(!isExpandFlags)}
                      >
                        <path
                          d='M10 12L6 8L10 4'
                          stroke='var(--text-classify)'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                        onClick={() => setIsExpandFlags(!isExpandFlags)}
                      >
                        <path
                          d='M4 6L8 10L12 6'
                          stroke='var(--text-classify)'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    )}
                  </div>
                </div>
                {isExpandFlags && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      width: '100%',
                      marginBottom: '1.5rem',
                    }}
                  >
                    <div>
                      <TariffToolInsightsPanel
                        htsHistory={htsHistory}
                        riskFlags={riskFlags}
                        hts_code={tariffInputs.htsCode}
                        questions={activeImportBan ? [] : exclusionQuestions}
                        enabledExclusions={enabledExclusions}
                        onAnswer={handleQuestionAnswer}
                        scopeQuestions={importBanQuestions}
                        onScopeAnswer={handleImportBanAnswer}
                        scopeTitle='Import Ban Applicability'
                        scopeBusy={loading}
                        showScreeningLink={false}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className='export-divider' style={{ display: 'flex' }} />

              <div className='classify-res-comp-sec-col'>
                <div className='classify-res-comp-sec'>
                  <div className='classify-res-comp-sec-left'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                    >
                      <path
                        d='M13.6666 4.85173L7.99998 7.99987M7.99998 7.99987L2.33331 4.85173M7.99998 7.99987L8 14.3332M14 10.7056V5.29419C14 5.06576 14 4.95155 13.9663 4.84968C13.9366 4.75956 13.8879 4.67684 13.8236 4.60705C13.7509 4.52816 13.651 4.47269 13.4514 4.36176L8.51802 1.62102C8.32895 1.51598 8.23442 1.46346 8.1343 1.44287C8.0457 1.42464 7.95431 1.42464 7.8657 1.44287C7.76559 1.46346 7.67105 1.51598 7.48198 1.62102L2.54865 4.36176C2.34896 4.47269 2.24912 4.52816 2.17642 4.60705C2.11211 4.67684 2.06343 4.75957 2.03366 4.84968C2 4.95155 2 5.06576 2 5.29419V10.7056C2 10.934 2 11.0482 2.03366 11.1501C2.06343 11.2402 2.11211 11.3229 2.17642 11.3927C2.24912 11.4716 2.34897 11.5271 2.54865 11.638L7.48198 14.3788C7.67105 14.4838 7.76559 14.5363 7.8657 14.5569C7.95431 14.5751 8.0457 14.5751 8.1343 14.5569C8.23442 14.5363 8.32895 14.4838 8.51802 14.3788L13.4514 11.638C13.651 11.5271 13.7509 11.4716 13.8236 11.3927C13.8879 11.3229 13.9366 11.2402 13.9663 11.1501C14 11.0482 14 10.934 14 10.7056Z'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M11 6.33333L5 3'
                        stroke='var(--text-classify)'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </div>
                  <div className='classify-res-comp-sec-txt'>
                    Product Description
                  </div>
                  <div className='classify-res-comp-sec-left'>
                    {!isExpandProd ? (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                        onClick={() => setIsExpandProd(!isExpandProd)}
                      >
                        <path
                          d='M10 12L6 8L10 4'
                          stroke='var(--text-classify)'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                        onClick={() => setIsExpandProd(!isExpandProd)}
                      >
                        <path
                          d='M4 6L8 10L12 6'
                          stroke='var(--text-classify)'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    )}
                  </div>
                </div>
                {isExpandProd && (
                  <ProductDescComp
                    tariffResults={tariffResults.data}
                    productValue={productValue}
                    dutyCost={dutyCost}
                    totalCost={totalCost}
                    baseCost={baseAmountDollars + basePercentDutyDollars}
                    displayAdditionalRate={liveAdditionalRate}
                    tariffCandidates={
                      tariffResults.data?.optionalExclusions ?? []
                    }
                    enabledExclusions={enabledExclusions}
                    toggleExclusion={toggleExclusion}
                    materialPct={tariffInputs.materialPct}
                    ftaOptions={tariffResults.data?.ftaOptions ?? []}
                    enabledFtas={enabledFtas}
                    toggleFta={toggleFTA}
                    explanationText={explanationText}
                    aiLoading={aiLoading}
                  />
                )}
              </div>

              <div className='export-divider' style={{ display: 'flex' }} />

              <div className='classify-res-comp-sec-col'>
                <div className='classify-res-comp-sec'>
                  <div className='classify-res-comp-sec-left'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='10'
                      height='15'
                      viewBox='0 0 10 15'
                      fill='none'
                    >
                      <path
                        d='M0.75 10.0833C0.75 11.5561 1.94391 12.75 3.41667 12.75H6.08333C7.55609 12.75 8.75 11.5561 8.75 10.0833C8.75 8.61057 7.55609 7.41667 6.08333 7.41667H3.41667C1.94391 7.41667 0.75 6.22276 0.75 4.75C0.75 3.27724 1.94391 2.08333 3.41667 2.08333H6.08333C7.55609 2.08333 8.75 3.27724 8.75 4.75M4.75 0.75V14.0833'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </div>
                  <div className='classify-res-comp-sec-txt'>
                    Duty Breakdown, Fees & Potential Exclusions
                  </div>
                  <div className='classify-res-comp-sec-left'>
                    {!isExpandSecNotes ? (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                        onClick={() => setIsExpandSecNotes(!isExpandSecNotes)}
                      >
                        <path
                          d='M10 12L6 8L10 4'
                          stroke='var(--text-classify)'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                        onClick={() => setIsExpandSecNotes(!isExpandSecNotes)}
                      >
                        <path
                          d='M4 6L8 10L12 6'
                          stroke='var(--text-classify)'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    )}
                  </div>
                </div>
                {isExpandSecNotes && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'stretch',
                      gap: '0px',
                    }}
                  >
                    <DutyComp
                      tariffResults={tariffResults.data}
                      productValue={productValue}
                      dutyCost={dutyCost}
                      totalCost={totalCost}
                      baseCost={baseAmountDollars + basePercentDutyDollars}
                      displayAdditionalRate={liveAdditionalRate}
                      tariffCandidates={
                        tariffResults.data?.optionalExclusions ?? []
                      }
                      enabledExclusions={enabledExclusions}
                      toggleExclusion={toggleExclusion}
                      materialPct={tariffInputs.materialPct}
                      ftaOptions={tariffResults.data?.ftaOptions ?? []}
                      enabledFtas={enabledFtas}
                      toggleFta={toggleFTA}
                      explanationText={explanationText}
                      aiLoading={aiLoading}
                    />
                    <CostSummaryComp
                      tariffResults={tariffResults.data}
                      productValue={productValue}
                      dutyCost={dutyCost}
                      totalCost={totalCost}
                      baseCost={baseAmountDollars + basePercentDutyDollars}
                      displayAdditionalRate={liveAdditionalRate}
                      tariffCandidates={
                        tariffResults.data?.optionalExclusions ?? []
                      }
                      enabledExclusions={enabledExclusions}
                      toggleExclusion={toggleExclusion}
                      materialPct={tariffInputs.materialPct}
                      ftaOptions={tariffResults.data?.ftaOptions ?? []}
                      enabledFtas={enabledFtas}
                      toggleFta={toggleFTA}
                      explanationText={explanationText}
                      aiLoading={aiLoading}
                    />
                  </div>
                )}
              </div>

              <div className='export-divider' style={{ display: 'flex' }} />

              <div className='classify-res-comp-sec-col'>
                <div className='classify-res-comp-sec'>
                  <div className='classify-res-comp-sec-left'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='15'
                      height='13'
                      viewBox='0 0 15 13'
                      fill='none'
                    >
                      <path
                        d='M7.41667 11.4167H2.88333C2.1366 11.4167 1.76323 11.4167 1.47801 11.2713C1.22713 11.1435 1.02316 10.9395 0.895325 10.6887C0.75 10.4034 0.75 10.0301 0.75 9.28333V2.88333C0.75 2.1366 0.75 1.76323 0.895325 1.47801C1.02316 1.22713 1.22713 1.02316 1.47801 0.895325C1.76323 0.75 2.1366 0.75 2.88333 0.75H3.15C4.64347 0.75 5.39021 0.75 5.96064 1.04065C6.46241 1.29631 6.87035 1.70426 7.12602 2.20603C7.41667 2.77646 7.41667 3.52319 7.41667 5.01667M7.41667 11.4167V5.01667M7.41667 11.4167H11.95C12.6967 11.4167 13.0701 11.4167 13.3553 11.2713C13.6062 11.1435 13.8102 10.9395 13.938 10.6887C14.0833 10.4034 14.0833 10.0301 14.0833 9.28333V2.88333C14.0833 2.1366 14.0833 1.76323 13.938 1.47801C13.8102 1.22713 13.6062 1.02316 13.3553 0.895325C13.0701 0.75 12.6967 0.75 11.95 0.75H11.6833C10.1899 0.75 9.44312 0.75 8.87269 1.04065C8.37093 1.29631 7.96298 1.70426 7.70732 2.20603C7.41667 2.77646 7.41667 3.52319 7.41667 5.01667'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </div>
                  <div className='classify-res-comp-sec-txt'>
                    Additional Notes
                  </div>
                  <div className='classify-res-comp-sec-left'>
                    {!isExpandCross ? (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                        onClick={() => setIsExpandCross(!isExpandCross)}
                      >
                        <path
                          d='M10 12L6 8L10 4'
                          stroke='var(--text-classify)'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                        onClick={() => setIsExpandCross(!isExpandCross)}
                      >
                        <path
                          d='M4 6L8 10L12 6'
                          stroke='var(--text-classify)'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    )}
                  </div>
                </div>

                {isExpandCross && (
                  <>
                    {/* {adcvdResults?.includes('This') &&
                      <div className='adcvd-container'>
                        <div className='adcvd-header'>
                          <ReportGmailerrorredIcon sx={{ color: '#f2d12c' }} />
                          <h2>Potential AD/CVD</h2>
                        </div>
                        <p>{adcvdResults}</p>
                      </div>
                    } */}
                    <ExpComp
                      tariffResults={tariffResults.data}
                      productValue={productValue}
                      dutyCost={dutyCost}
                      totalCost={totalCost}
                      baseCost={baseAmountDollars + basePercentDutyDollars}
                      displayAdditionalRate={liveAdditionalRate}
                      tariffCandidates={
                        tariffResults.data?.optionalExclusions ?? []
                      }
                      enabledExclusions={enabledExclusions}
                      toggleExclusion={toggleExclusion}
                      materialPct={tariffInputs.materialPct}
                      ftaOptions={tariffResults.data?.ftaOptions ?? []}
                      enabledFtas={enabledFtas}
                      toggleFta={toggleFTA}
                      explanationText={explanationText}
                      aiLoading={aiLoading}
                      aiExplain={aiExplain}
                    />
                  </>
                )}
              </div>
            </>
          )
        )}
      </div>
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <CreateTicketWizard
          cur_user={cur_user}
          setFrequency={setFrequency}
          frequency={frequency}
          dateStrategy={dateStrategy}
          setDateStrategy={setDateStrategy}
          handleSaveTicket={createTicket}
          newTicketName={newTicketName || ticketDraft.ticketName}
          setNewTicketName={setNewTicketName}
          shareEmail={shareEmail}
          setShareEmail={setShareEmail}
          savingTicket={saving}
        />
      </Modal>
    </>
  );
};

export default TariffResultsComp;
